import { useEffect, useState } from 'react'
import { useEquipmentScope } from './equipmentScope'
import { AlertCircle, Check, Layers, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { EquipmentGroupRow } from '@/hooks/useGroups'

/**
 * Add / rename / delete equipment groups. Group names are unique per tenant and
 * double as the display label. Deleting a group that still has equipment types
 * fails server-side (409) — the message is surfaced inline.
 */
export function ManageGroupsModal({
  open,
  onClose,
  groups,
  onAdd,
  onRename,
  onRemove,
}: {
  open: boolean
  onClose: () => void
  groups: EquipmentGroupRow[]
  onAdd: (name: string) => Promise<void>
  onRename: (id: string, name: string) => Promise<void>
  onRemove: (id: string) => Promise<void>
}) {
  const { copy } = useEquipmentScope()
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')

  useEffect(() => {
    if (open) {
      setNewName('')
      setError(null)
      setBusy(false)
      setEditingId(null)
      setEditName('')
    }
  }, [open])

  const run = async (op: Promise<void>) => {
    setError(null)
    setBusy(true)
    try {
      await op
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleAdd = async () => {
    const name = newName.trim()
    if (!name || busy) return
    if (await run(onAdd(name))) setNewName('')
  }

  const startEdit = (group: EquipmentGroupRow) => {
    setError(null)
    setEditingId(group.id)
    setEditName(group.name)
  }

  const handleRename = async () => {
    if (editingId === null) return
    const name = editName.trim()
    if (!name || busy) return
    if (await run(onRename(editingId, name))) setEditingId(null)
  }

  const handleRemove = async (id: string) => {
    if (busy) return
    await run(onRemove(id))
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <div className='flex items-start gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
            <Layers className='h-5 w-5' />
          </div>
          <div>
            <h2 className='text-base font-semibold text-gray-900'>Manage Groups</h2>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              Add, rename, or delete {copy.unitLower} groups. A group must be empty before it can be
              deleted.
            </p>
          </div>
        </div>

        {error && (
          <div className='mt-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        <div className='mt-4 flex items-end gap-2'>
          <div className='flex-1 space-y-1'>
            <label className='text-xs font-medium text-gray-600'>New group</label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder='e.g. Quality Control'
            />
          </div>
          <Button onClick={handleAdd} disabled={busy || newName.trim() === ''} className='gap-1.5'>
            {busy ? <Loader2 className='h-4 w-4 animate-spin' /> : <Plus className='h-4 w-4' />}
            Add
          </Button>
        </div>

        <div className='mt-4 max-h-[45vh] space-y-1.5 overflow-y-auto pr-1'>
          {groups.length === 0 ? (
            <p className='py-6 text-center text-sm text-gray-400'>No groups yet. Add one above.</p>
          ) : (
            groups.map((group) => (
              <div
                key={group.id}
                className='flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-2'
              >
                {editingId === group.id ? (
                  <>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                      className='h-8 flex-1'
                      autoFocus
                    />
                    <button
                      type='button'
                      onClick={handleRename}
                      disabled={busy || editName.trim() === ''}
                      title='Save'
                      className='text-emerald-600 transition-colors hover:text-emerald-700 disabled:opacity-40'
                    >
                      <Check className='h-4 w-4' />
                    </button>
                    <button
                      type='button'
                      onClick={() => setEditingId(null)}
                      title='Cancel'
                      className='text-gray-400 transition-colors hover:text-gray-600'
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </>
                ) : (
                  <>
                    <span className='min-w-0 flex-1 truncate text-sm text-gray-800'>
                      {group.name}
                    </span>
                    <button
                      type='button'
                      onClick={() => startEdit(group)}
                      disabled={busy}
                      title='Rename'
                      className='text-gray-400 transition-colors hover:text-blue-600 disabled:opacity-40'
                    >
                      <Pencil className='h-4 w-4' />
                    </button>
                    <button
                      type='button'
                      onClick={() => handleRemove(group.id)}
                      disabled={busy}
                      title='Delete'
                      className='text-gray-400 transition-colors hover:text-destructive disabled:opacity-40'
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <DialogFooter className='mt-5'>
          <Button variant='outline' onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
